import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Typography } from '@mui/material';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import DepartmentSelection from './components/DepartmentSelection';
import MarketingDashboard from './components/MarketingDashboard';
import History from './components/History';
import Reports from './components/Reports';
import SimpleLivestreamScheduler from './components/SimpleLivestreamScheduler';
import TaskManagement from './components/marketing/TaskManagement';
import ContentManager from './components/marketing/ContentManager';
import About from './components/About';
import PrivacyPolicy from './components/PrivacyPolicy';
import { getUserData } from './services/storageService';
import AgreementModal from './components/AgreementModal';
import BonusPopup from './components/BonusPopup';
import PayrollReadyToast from './components/PayrollReadyToast';

const theme = createTheme({
  palette: {
    primary: {
      main: '#FF6B6B',
    },
    secondary: {
      main: '#FF8E53',
    },
  },
});

const PrivateRoute = ({ children, adminOnly = false, departmentRequired = false, requiredDepartment = null }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = getUserData();
    setUser(userData);
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #059669 100%)'
        }}
      >
        <CircularProgress size={40} sx={{ color: 'white', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'white' }}>
          Đang kiểm tra quyền truy cập...
        </Typography>
      </Box>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/department-selection" replace />;
  }

  // Nếu route yêu cầu department và user không phải admin
  if (departmentRequired && user.role !== 'admin') {
    // Phải có currentDepartment
    if (!user.currentDepartment) {
      return <Navigate to="/department-selection" replace />;
    }
    
    // Nếu có yêu cầu department cụ thể, kiểm tra permissions
    if (requiredDepartment) {
      const requiredRole = `${requiredDepartment}_dept`;
      if (!user.departmentRoles || !user.departmentRoles.includes(requiredRole)) {
        return <Navigate to="/department-selection" replace />;
      }
      
      // Kiểm tra currentDepartment có match với required department không
      if (user.currentDepartment !== requiredDepartment) {
        return <Navigate to="/department-selection" replace />;
      }
    }
  }

  return (
    <>
      {!adminOnly && user.role !== 'admin' && <AgreementModal />}
      {!adminOnly && user.role !== 'admin' && <BonusPopup />}
      {!adminOnly && user.role !== 'admin' && <PayrollReadyToast />}
      {children}
    </>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/department-selection"
            element={
              <PrivateRoute>
                <DepartmentSelection />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute departmentRequired requiredDepartment="livestream">
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/history"
            element={
              <PrivateRoute departmentRequired requiredDepartment="livestream">
                <History />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/reports"
            element={
              <PrivateRoute departmentRequired requiredDepartment="livestream">
                <Reports />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/schedule"
            element={
              <PrivateRoute departmentRequired requiredDepartment="livestream">
                <SimpleLivestreamScheduler />
              </PrivateRoute>
            }
          />
          <Route
            path="/marketing-dashboard"
            element={
              <PrivateRoute departmentRequired requiredDepartment="marketing">
                <MarketingDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/marketing-dashboard/tasks"
            element={
              <PrivateRoute departmentRequired requiredDepartment="marketing">
                <TaskManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/marketing-dashboard/content"
            element={
              <PrivateRoute departmentRequired requiredDepartment="marketing">
                <ContentManager />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute adminOnly>
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/about"
            element={
              <PrivateRoute>
                <About />
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;
